import { gql, useLazyQuery } from "@apollo/client";
import { AlertTypeEnums, IFormState, ReactStripe } from "@canei/app-components";
import { useMemo, useState } from "react";
import { initialPaymentFormData, IPaymentFormProps } from "../../../components/PaymentForm";
import { ETriState } from "../../../quick/Private/Setup";
import { useTranslation } from "react-i18next";
import { ECountry } from "./appStoreTypes.d";
import { useSelector } from "react-redux";
import { ILocalState } from "../../../@types";

// Regular expression to validate vat_id
// const vatIdRegExp = new RegExp(
//   "^(ATU[0-9]{8}|BE[01][0-9]{9}|BG[0-9]{9,10}|HR[0-9]{11}|(CY)?[0-9]{8}[A-Z]|CZ[0-9]{8,10}|DK[0-9]{8}|EE[0-9]{9}|FI[0-9]{8}|FR[0-9A-Z]{2}[0-9]{9}|EL[0-9]{9}|HU[0-9]{8}|IE([0-9]{7}[A-Z]{1,2}|[0-9][A-Z][0-9]{5}[A-Z])|IT[0-9]{11}|LV[0-9]{11}|LT([0-9]{9}|[0-9]{12})|LU[0-9]{8}|MT[0-9]{8}|NL[0-9]{9}B[0-9]{2}|PL[0-9]{10}|PT[0-9]{9}|RO[0-9]{2,10}|SK[0-9]{10}|SI[0-9]{8}|ES[A-Z0-9]{9}|SE[0-9]{12}|GB([0-9]{9}|[0-9]{12}|GD[0-4][0-9]{2}|HA[5-9][0-9]{2})|CHE-[0-9]{3}\\.[0-9]{3}\\.[0-9]{3}(?:\\s(?:MWST|TVA|IVA|VAT))?|CHE[0-9]{9}(?:\\s(?:MWST|TVA|IVA|VAT))?|DE[0-9]{9}|DE[0-9]{9}-[0-9]{5}|[0-9]{2}\\/[0-9]{3}\\/[0-9]{5}|[0-9]{10,11}|[0-9]{2}-[0-9]{3}\\/[0-9]{4}|[0-9]{9})$"
// );
const vatIdRegEx: Record<keyof typeof ECountry, RegExp> = {
  DEU: /^(DE[0-9]{9}|DE[0-9]{9}-[0-9]{5}|[0-9]{2}\/[0-9]{3}\/[0-9]{5})$/,
  AUT: /^(ATU[0-9]{8}|[0-9]{2}-[0-9]{3}\/[0-9]{4})$/,
  CHE: /^(CHE-[0-9]{3}\.[0-9]{3}\.[0-9]{3}(?:\s(?:MWST|TVA|IVA|VAT))?|CHE[0-9]{9}(?:\s(?:MWST|TVA|IVA|VAT))?)$/,
};

const initialBillingData: IBillingDetails = {
  name: "",
  email: "",
  address: { line1: "", line2: "", city: "", postal_code: "", vat_id: "" },
};

const { useElements, useStripe, CardNumberElement, CardCvcElement, IbanElement } = ReactStripe;

const GET_STRIPE_INTENT = gql`
  query getIntent($customer_id: ID!, $type: PaymentMethodTypes, $covId: ID!) {
    result: getIntent(customer_id: $customer_id, type: $type, covId: $covId) {
      client_secret
      type
    }
  }
`;

export interface IGetIntentResult {
  result: IGetIntentData;
}

export interface IGetIntentData {
  client_secret: string;
  type: string;
}
export interface IGetIntentVars {
  customer_id: string;
  type?: EPaymentMethods;
  covId?: string;
}

export enum EPaymentMethods {
  card = "card",
  sepa = "sepa_debit",
}

export interface IUseMakePaymentRetVal {
  makePayment: (variables: IGetIntentVars) => void;
  updateBillingDetails: (form: IFormState) => void;
  canBeSubmitted: boolean;
  paymentFormData: IPaymentFormProps;
  billingDetails: IBillingDetails;
  isReady: boolean;
  isLoading: boolean;
  updateBilling: boolean;
  callUpdateBilling: (state: boolean) => void;
  validateVatID: (value: string, country: string) => boolean;
}
export interface IBillingDetails {
  name: string;
  email: string;
  address: IBillingAddress;
}

export interface IBillingAddress {
  line1?: string;
  line2?: string;
  city?: string;
  postal_code?: string;
  country?: ECountry | string;
  vat_id?: string;
}

// to convert the selected country (which consist of 3 letters) into 2 letters which is expected by stripe (stripe accepts 2 letters)
const countryAlpha3ToAlpha2: Record<ECountry, string> = {
  [ECountry.AUT]: "AT", // Austria
  [ECountry.DEU]: "DE", // Germany
  [ECountry.CHE]: "CH", // Switzerland
};
const getStripeCountryCode = (country: ECountry): string => countryAlpha3ToAlpha2[country];

export const useMakePayment = (): IUseMakePaymentRetVal => {
  const { t } = useTranslation(["quick/common"]);
  const [isLoading, setIsLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<EPaymentMethods | undefined>(undefined);
  const [billingDetails, setBillingDetails] = useState<IBillingDetails>(initialBillingData);
  const [paymentFormData, setPaymentFormData] = useState<IPaymentFormProps>(initialPaymentFormData);
  const [updateBilling, setUpdateBilling] = useState<boolean>(false);
  const { appStore } = useSelector<ILocalState, ILocalState>((state) => state);

  const elements = useElements();
  const stripe = useStripe();

  const callUpdateBilling = (state: boolean): void => {
    setUpdateBilling(state);
  };

  const isReady = useMemo((): boolean => {
    return elements !== null && stripe !== null;
  }, [elements, stripe]);

  const intentReady = ({ result }: IGetIntentResult): void => {
    if (!stripe || !elements || !paymentMethod || !billingDetails) return;
    const card = elements.getElement(CardNumberElement);
    const cvc = elements.getElement(CardCvcElement);
    const iban = elements.getElement(IbanElement);

    billingDetails.address.country = getStripeCountryCode(
      billingDetails.address.country as ECountry
    );
    delete billingDetails.address.vat_id;

    if (result.type === "payment_intent") {
      if (paymentMethod === "card" && card !== null && cvc !== null) {
        stripe
          .confirmCardPayment(
            result.client_secret,
            {
              payment_method: {
                card,
                billing_details: {
                  ...billingDetails,
                },
              },
              setup_future_usage: "off_session",
              return_url: window.location.href,
            },
            { handleActions: false }
          )
          .then(({ error, paymentIntent }) => {
            setIsLoading(false);
            // TODO: if(error) show error dialog.
            // TODO: if paymentIntent succeeded - check refreshClient ready state. To redirect to dashboard

            if (error?.message) {
              setPaymentFormData({
                ...paymentFormData,
                state: { type: AlertTypeEnums.ERROR, message: error.message },
              });
            }
            if (paymentIntent?.status === "succeeded") {
              setPaymentFormData({
                ...paymentFormData,
                state: {
                  type: AlertTypeEnums.SUCCESS,
                  message: t("payment.update_succeeded"),
                },
              });
              setUpdateBilling(true);
            }
            if (paymentIntent?.status === "requires_action") {
              window.location = paymentIntent.next_action?.redirect_to_url
                ?.url as unknown as Location;
            }
          })
          .catch((err) => {
            setIsLoading(false);
          });
      }

      if (paymentMethod === "sepa_debit" && iban !== null) {
        stripe
          .confirmSepaDebitPayment(result.client_secret, {
            payment_method: {
              sepa_debit: iban,
              billing_details: {
                ...billingDetails,
              },
            },
            setup_future_usage: "off_session",
            return_url: window.location.href,
          })
          .then(({ error, paymentIntent }) => {
            setIsLoading(false);
            // TODO: if(error) show error dialog.
            // TODO: if paymentIntent succeeded - check refreshClient ready state. To redirect to dashboard
            if (error?.message) {
              setPaymentFormData({
                ...paymentFormData,
                state: { type: AlertTypeEnums.ERROR, message: error.message },
              });
            }
            if (paymentIntent?.status === "succeeded") {
              setPaymentFormData({
                ...paymentFormData,
                state: {
                  type: AlertTypeEnums.SUCCESS,
                  message: t("payment.update_succeeded"),
                },
              });
              setUpdateBilling(true);
            }
            if (paymentIntent?.status === "requires_action") {
              window.location = paymentIntent.next_action?.redirect_to_url
                ?.url as unknown as Location;
            }
          })
          .catch((err) => {
            setIsLoading(false);
          });
      }
    } else if (result.type === "setup_intent") {
      if (paymentMethod === "card" && card !== null && cvc !== null) {
        stripe
          .confirmCardSetup(
            result.client_secret,
            {
              payment_method: {
                card,
                billing_details: {
                  ...billingDetails,
                },
              },
              return_url: window.location.href,
            },
            { handleActions: false }
          )
          .then(({ error, setupIntent }) => {
            setIsLoading(false);
            // TODO: if(error) show error dialog.
            // TODO: if setupIntent succeeded - check refreshClient ready state. To redirect to dashboard
            if (error?.message) {
              setPaymentFormData({
                ...paymentFormData,
                state: { type: AlertTypeEnums.ERROR, message: error.message },
              });
            }
            if (setupIntent?.status === "succeeded") {
              setPaymentFormData({
                ...paymentFormData,
                state: {
                  type: AlertTypeEnums.SUCCESS,
                  message: t("payment.update_succeeded"),
                },
              });
              setUpdateBilling(true);
            }
            if (setupIntent?.status === "requires_action") {
              window.location = setupIntent.next_action?.redirect_to_url
                ?.url as unknown as Location;
            }
          })
          .catch((err) => {
            setIsLoading(false);
          });
      }

      if (paymentMethod === "sepa_debit" && iban !== null) {
        stripe
          .confirmSepaDebitSetup(result.client_secret, {
            payment_method: {
              sepa_debit: iban,
              billing_details: {
                ...billingDetails,
              },
            },
            return_url: window.location.href,
          })
          .then(({ error, setupIntent }) => {
            setIsLoading(false);
            // TODO: if(error) show error dialog.
            // TODO: if setupIntent succeeded - check refreshClient ready state. To redirect to dashboard
            if (error?.message) {
              setPaymentFormData({
                ...paymentFormData,
                state: { type: AlertTypeEnums.ERROR, message: error.message },
              });
            }
            if (setupIntent?.status === "succeeded") {
              setPaymentFormData({
                ...paymentFormData,
                state: {
                  type: AlertTypeEnums.SUCCESS,
                  message: t("payment.update_succeeded"),
                },
              });
              setUpdateBilling(true);
            }
            if (setupIntent?.status === "requires_action") {
              window.location = setupIntent.next_action?.redirect_to_url
                ?.url as unknown as Location;
            }
          })
          .catch((err) => {
            setIsLoading(false);
          });
      }
    }
  };

  // to validate vat id in billing address
  const validateVatID = (vatID: string, country: string): boolean => {
    let isVatIdValid = false;
    if (country === ECountry.DEU && !vatID) isVatIdValid = true;
    else if (!vatID) isVatIdValid = false;
    else isVatIdValid = vatIdRegEx[country as keyof typeof ECountry].test(vatID);
    return isVatIdValid;
  };

  const [getIntent] = useLazyQuery<IGetIntentResult, IGetIntentVars>(GET_STRIPE_INTENT, {
    onCompleted: intentReady,
    fetchPolicy: "network-only",
  });
  const canBeSubmitted = useMemo((): boolean => {
    const country = billingDetails.address.country;
    const vatID = billingDetails.address.vat_id;
    let billingNameFieldValidation = false;
    if (appStore.paymentType === EPaymentMethods.sepa && billingDetails.name.length >= 3)
      billingNameFieldValidation = true;
    if (appStore.paymentType === EPaymentMethods.card && billingDetails.name !== "")
      billingNameFieldValidation = true;

    return (
      billingDetails.address.line1 !== "" &&
      billingDetails.address.line2 !== "" &&
      billingDetails.address.postal_code !== "" &&
      billingDetails.address.city !== "" &&
      billingDetails.address.country !== "" &&
      billingDetails.name !== "" &&
      billingDetails.email !== "" &&
      billingNameFieldValidation &&
      validateVatID(vatID as string, country as string)
    );
  }, [
    billingDetails.address.city,
    billingDetails.address.country,
    billingDetails.address.line1,
    billingDetails.address.line2,
    billingDetails.address.postal_code,
    billingDetails.email,
    billingDetails.name,
    billingDetails.address.vat_id,
    appStore.paymentType,
  ]);

  const makePayment = (variables: IGetIntentVars): void => {
    if (!isReady) return;
    if (!canBeSubmitted) {
      setPaymentFormData({
        ...paymentFormData,
        valid: {
          ...paymentFormData.valid,
          line1: billingDetails.address.line1 === "" ? ETriState.FALSE : ETriState.TRUE,
          line2: billingDetails.address.line2 === "" ? ETriState.FALSE : ETriState.TRUE,
          name: billingDetails.name === "" ? ETriState.FALSE : ETriState.TRUE,
          email: billingDetails.email === "" ? ETriState.FALSE : ETriState.TRUE,
          postal_code: billingDetails.address.postal_code === "" ? ETriState.FALSE : ETriState.TRUE,
          city: billingDetails.address.city === "" ? ETriState.FALSE : ETriState.TRUE,
          country: billingDetails.address.country === "" ? ETriState.FALSE : ETriState.TRUE,
        },
      });
      return;
    }
    setIsLoading(true);
    setPaymentMethod(variables.type);
    getIntent({ variables });
  };

  const updateBillingDetails = (form: IFormState): void => {
    const isBillingDetailsChanged =
      form.line1 !== billingDetails.address?.line1 ||
      form.line2 !== billingDetails.address?.line2 ||
      form.postal_code !== billingDetails.address?.postal_code ||
      form.city !== billingDetails.address?.city ||
      form.country !== billingDetails.address?.country ||
      form.email !== billingDetails.email ||
      form.name !== billingDetails.name ||
      form.vat_id !== billingDetails.address.vat_id;

    if (!isBillingDetailsChanged) return;

    setBillingDetails({
      ...billingDetails,
      name: form.name,
      email: form.email,
      address: {
        ...billingDetails.address,
        line1: form.line1,
        line2: form.line2,
        postal_code: form.postal_code,
        city: form.city,
        country: form.country as ECountry,
        vat_id: form.vat_id,
      },
    });
  };

  return {
    makePayment,
    updateBillingDetails,
    billingDetails,
    paymentFormData,
    canBeSubmitted,
    isReady,
    isLoading,
    updateBilling,
    callUpdateBilling,
    validateVatID,
  };
};
